/**
 * The design lab (`/playChessLab`, hidden from the drawer).
 *
 * It renders the SAME `<PlayChessV2Body />` the real screen renders — not a
 * copy — inside a fixed-width frame, with:
 *   · a theme toggle (light / dark / CircleChess) and a viewport toggle
 *     (desktop / phone),
 *   · a fixture chip per store snapshot,
 *   · an "Opponent moves" button that plays a random legal move through the
 *     REAL reducer, so both sides can be played by hand,
 *   · the outgoing packets, because the socket is faked.
 *
 * It deliberately does NOT call `useGameplayHandlers` / `useGameplayEffects`:
 * there is no socket here, and the effects would try to send
 * `end_game_confirmed` into the void.
 *
 * The route name is `playChessLab` on purpose: SocketProvider's redirect guard
 * lowercases the pathname and skips any path containing '/playchess'
 * (SocketProvider.tsx L289-292), so the provider never navigates away from it.
 */
import React, {useCallback, useEffect, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';

import {liveGameStore} from '@/Store/liveGameStore';

import PlayChessV2Body from '../Body';
import {readRenderCounts, resetRenderCounts} from '../stage/renderCount';
import {SPACE} from '../theme/tokens';
import {useV2Theme} from '../theme/useV2Theme';
import {Chip, Txt} from '../ui';
import {useV2Ui} from '../theme/useV2Ui';
import {
  FIXTURE_LABELS,
  FIXTURE_NAMES,
  FixtureName,
  buildFixture,
} from './fixtures';
import {useLabStore, installFakeSocket} from './labStore';
import {
  endGameAsCheckmate,
  offerDrawAsOpponent,
  playRandomMove,
  rejectDrawAsOpponent,
  toggleOpponentDisconnected,
} from './opponent';

/**
 * The phone frame is a device box; the DESKTOP frame is fluid, because the
 * real screen is fluid — a fixed 1280x800 lab frame let a layout look right
 * here and wrong at 1512x982. Body measures whatever box it is given, so a
 * '100%' frame behaves exactly like the real content area.
 */
const PHONE_FRAME = {width: 390, height: 844};
/** the lab's own chrome above the frame (bar + fixtures + counters) */
const LAB_CHROME = 190;

const desktopFrameHeight = (): number => {
  if (typeof window === 'undefined') {
    return 800;
  }
  return Math.max(560, (window.innerHeight || 900) - LAB_CHROME);
};

/** the lab's chrome is `ui/Chip`'s pill: active = the primary fill */
const LabChip = React.memo(function LabChip({
  label,
  active,
  onPress,
}: {
  label: string;
  active?: boolean;
  onPress: () => void;
}) {
  return (
    <Chip label={label} variant="pill" active={active} onPress={onPress} />
  );
});

function LabScreen() {
  const tokens = useV2Theme(s => s.tokens);
  const themeName = useV2Theme(s => s.name);
  const setTheme = useV2Theme(s => s.setTheme);
  const viewport = useV2Ui(s => s.viewport);
  const fixture = useLabStore(s => s.fixture);
  const setFixture = useLabStore(s => s.setFixture);
  const sent = useLabStore(s => s.sent);
  const clearSent = useLabStore(s => s.clear);

  const [showPackets, setShowPackets] = useState(true);
  const [counters, setCounters] = useState(() => readRenderCounts());

  const apply = useCallback(
    (name: FixtureName) => {
      const snapshot = buildFixture(name);
      setFixture(name);
      // `true` = REPLACE: a fixture is a whole state, not a patch. The store's
      // own functions (applyMessage/actions/…) are re-attached by zustand
      // because they live on the same object — so re-seed them explicitly.
      const store = liveGameStore.getState();
      liveGameStore.setState(
        {
          ...snapshot,
          applyMessage: store.applyMessage,
          setSession: store.setSession,
          reset: store.reset,
          actions: store.actions,
        },
        true,
      );
      liveGameStore.getState().setSession(snapshot.session);
    },
    [setFixture],
  );

  // one-time wiring: the fake socket and the first fixture
  useEffect(() => {
    installFakeSocket();
    apply('liveMyMove');
  }, [apply]);

  // Body measures the frame with onLayout and writes viewport/rail/board
  // itself — the lab only has to hand it a box of the right shape.
  const [frameHeight, setFrameHeight] = useState(desktopFrameHeight);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const onResize = () => setFrameHeight(desktopFrameHeight());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCounters(readRenderCounts()), 500);
    return () => clearInterval(interval);
  }, []);

  const setViewport = useV2Ui(s => s.setViewport);

  return (
    <ScrollView
      style={[styles.page, {backgroundColor: tokens.bg}]}
      contentContainerStyle={styles.pageContent}>
      <View style={styles.bar}>
        <Txt variant="labTitle" style={styles.title}>
          PlayChess V2 · design lab
        </Txt>

        <View style={styles.group}>
          <LabChip
            label="Light"
            active={themeName === 'light'}
            onPress={() => setTheme('light')}
          />
          <LabChip
            label="Dark"
            active={themeName === 'dark'}
            onPress={() => setTheme('dark')}
          />
          <LabChip
            label="CircleChess"
            active={themeName === 'circlechess'}
            onPress={() => setTheme('circlechess')}
          />
        </View>

        <View style={styles.group}>
          <LabChip
            label="Desktop"
            active={viewport === 'desktop'}
            onPress={() => setViewport('desktop')}
          />
          <LabChip
            label="Phone"
            active={viewport === 'phone'}
            onPress={() => setViewport('phone')}
          />
        </View>

        <View style={styles.group}>
          <LabChip label="Opponent moves" onPress={() => playRandomMove()} />
          <LabChip
            label="Offer draw as opponent"
            onPress={offerDrawAsOpponent}
          />
          <LabChip label="Decline my draw" onPress={rejectDrawAsOpponent} />
          <LabChip label="End: checkmate" onPress={endGameAsCheckmate} />
          <LabChip
            label="Toggle disconnect"
            onPress={toggleOpponentDisconnected}
          />
          <LabChip
            label="Reset"
            onPress={() => apply(fixture as FixtureName)}
          />
        </View>
      </View>

      <View style={styles.fixtures}>
        {FIXTURE_NAMES.map(name => (
          <LabChip
            key={name}
            label={FIXTURE_LABELS[name]}
            active={fixture === name}
            onPress={() => apply(name)}
          />
        ))}
      </View>

      <View style={styles.counters}>
        <Txt variant="caption" tone="muted" style={styles.countersText}>
          {Object.keys(counters).length === 0
            ? 'renders: —'
            : `renders: ${Object.keys(counters)
                .sort()
                .map(key => `${key} ${counters[key]}`)
                .join('  ·  ')}`}
        </Txt>
        <LabChip
          label="Reset counters"
          onPress={() => {
            resetRenderCounts();
            setCounters(readRenderCounts());
          }}
        />
      </View>

      <View
        style={[
          styles.frame,
          viewport === 'phone'
            ? ({
                width: PHONE_FRAME.width,
                height: PHONE_FRAME.height,
                alignSelf: 'flex-start',
              } as any)
            : ({
                width: '100%',
                height: frameHeight,
                alignSelf: 'stretch',
              } as any),
          {backgroundColor: tokens.bg, boxShadow: tokens.sh2} as any,
        ]}>
        <PlayChessV2Body />
      </View>

      <View style={styles.packets}>
        <View style={styles.packetsHead}>
          <LabChip
            label={`Sent packets (${sent.length})`}
            onPress={() => setShowPackets(!showPackets)}
          />
          <LabChip label="Clear" onPress={clearSent} />
        </View>
        {showPackets &&
          sent.map(entry => (
            <Txt
              key={`${entry.at}-${entry.type}-${sent.indexOf(entry)}`}
              variant="labPacket"
              tone="ink2">
              {`${entry.type}  ${JSON.stringify(entry.packet)}`}
            </Txt>
          ))}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
  },
  pageContent: {
    padding: 20,
    gap: 12,
  },
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  title: {
    marginRight: SPACE.sm,
  },
  group: {
    flexDirection: 'row',
    gap: 6,
  },
  fixtures: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  counters: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  countersText: {
    flex: 1,
  },
  frame: {
    borderRadius: 10,
    overflow: 'hidden',
  },
  packets: {
    gap: 4,
  },
  packetsHead: {
    flexDirection: 'row',
    gap: 6,
  },
});

export default LabScreen;
